import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Icon,
  SimpleGrid,
  useColorModeValue,
  useColorMode,
  Text,
} from '@chakra-ui/react';

import { IoIosCalendar } from 'react-icons/io';
import { BiTransfer } from 'react-icons/bi';

import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import ComplexTable from 'views/admin/default/components/ComplexTable';
import DailyLoginsRecentMonths from './components/DailyLoginsRecentMonths';
import MonthlyTransfersCountChart from './components/MonthlyTransfersCountChart';
import PieCard from 'views/admin/default/components/PieCard';
import TotalTransfersRecentMonths from './components/TotalTransferInFourMonth';
import { columnsDataComplex } from 'views/admin/default/variables/columnsData';
import AuthApi from '../../../api/auth';
import AnalyticsApi from '../../../api/analytics';
import User from '../../../api/user';

export default function UserReports() {
  // Chakra Color Mode
  const brandColor = useColorModeValue('brand.600', 'white');
  const boxBg = useColorModeValue('brand.100', 'whiteAlpha.100');

  // Colorful icon backgrounds for different stat cards
  const greenBg = useColorModeValue('linear-gradient(135deg, #18E482 0%, #01B574 100%)', 'whiteAlpha.100');
  const blueBg = useColorModeValue('linear-gradient(135deg, #4299E1 0%, #3182CE 100%)', 'whiteAlpha.100');
  const purpleBg = useColorModeValue('linear-gradient(135deg, #9F7AEA 0%, #805AD5 100%)', 'whiteAlpha.100');
  const orangeBg = useColorModeValue('linear-gradient(135deg, #F6AD55 0%, #ED8936 100%)', 'whiteAlpha.100');
  const tealBg = useColorModeValue('linear-gradient(135deg, #38B2AC 0%, #319795 100%)', 'whiteAlpha.100');
  const pinkBg = useColorModeValue('linear-gradient(135deg, #ED64A6 0%, #D53F8C 100%)', 'whiteAlpha.100');

  const [transfersToday, setTransfersToday] = useState('₪0');
  const [transfersMonth, setTransfersMonth] = useState('₪0');
  const [transfersPreviousMonth, setTransfersPreviousMonth] = useState('₪0');
  const [monthlyTransfersHistory, setMonthlyTransfersHistory] = useState([]);
  const [monthlyTransfersCountHistory, setMonthlyTransfersCountHistory] = useState([]);
  const [monthlyLoginsHistory, setMonthlyLoginsHistory] = useState([]);
  // Non-calendar x-axis labels (e.g. Xtras 26→25 period strings). Null when the viewer
  // is on calendar months — charts fall back to getRecentMonths() in that case.
  const [monthlyHistoryLabels, setMonthlyHistoryLabels] = useState(null);
  const [transfersAvgMonth, setTransfersAvgMonth] = useState('₪0');
  const [historicalTransfersAvg, setHistoricalTransfersAvg] = useState('₪0');
  const [activeUsers, setActiveUsers] = useState(0);
  const [nonActiveUsers, setNonActiveUsers] = useState(0);
  const [transfersCount, setTransfersCount] = useState(0);
  const [usersTransferredCount, setUsersTransferredCount] = useState(0);
  const [dakot2RealTransfersCount, setDakot2RealTransfersCount] = useState(null);
  const [dakot2SplitTransfersCount, setDakot2SplitTransfersCount] = useState(null);

  const [transfersAvgMonthPreviousMonth, setTransfersAvgMonthPreviousMonth] = useState('₪0');
  const [transfersCountPreviousMonth, setTransfersCountPreviousMonth] = useState(0);
  const [usersTransferredCountPreviousMonth, setUsersTransferredCountPreviousMonth] = useState(0);

  const [recentTransfers, setRecentTransfers] = useState([]);
  const { colorMode, toggleColorMode } = useColorMode();

  // Check if user has SUPER_ADMIN access (type === 1 or has specific admin screens)
  const userInfo = User.GetInfo();
  const isSuperAdmin = userInfo && (userInfo.type === 1 );
  // Global-view roles (SuperAdmin, SuperAnalytics, SuperFinance, CustomerSupport)
  // all have the 'companies' screen; scoped roles (Admin/OrgAdmin/PayrollCoordinator) don't.
  const hasGlobalView = !!(userInfo && userInfo.screens && userInfo.screens.includes('companies'));

  const circleFontSize = (text) => {
    const len = String(text).length;
    if (len <= 3) return '22px';
    if (len === 4) return '20px';
    if (len === 5) return '17px';
    if (len === 6) return '15px';
    return '13px';
  };

  const usersTransferredCountStr = usersTransferredCount.toLocaleString('en-US');
  const transfersCountStr = (isSuperAdmin && dakot2RealTransfersCount != null
    ? transfersCount + dakot2SplitTransfersCount - dakot2RealTransfersCount
    : transfersCount
  ).toLocaleString('en-US');

  const getUserInfo = () => {
    let userInfo = JSON.parse(localStorage.getItem('user'));
    let accessToken = userInfo.token;

    AuthApi.GetUserInfo(accessToken)
      .then((res) => {})
      .catch((e) => {
        if (e.response && e.response.status == 401) {
        }
      });
  };

  const getAnalytics = () => {
    AnalyticsApi.getAnalytics()
      .then((res) => {
        if (res.data) {
          let analytics = res.data;
          if (analytics.dailySum) {
            setTransfersToday('₪' + Math.floor(analytics.dailySum).toLocaleString('en-US'));
          }
          if (analytics.monthlySum) {
            setTransfersMonth('₪' + Math.floor(analytics.monthlySum).toLocaleString('en-US'));
          }
          if (analytics.monthlyTransfersHistory) {
            setMonthlyTransfersHistory(analytics.monthlyTransfersHistory);
          }
          if (analytics.monthlyTransfersCountHistory) {
            setMonthlyTransfersCountHistory(analytics.monthlyTransfersCountHistory);
          }
          if (Array.isArray(analytics.monthlyHistoryLabels) && analytics.monthlyHistoryLabels.length > 0) {
            setMonthlyHistoryLabels(analytics.monthlyHistoryLabels);
          }
          if (analytics.monthlyAmountAvg) {
            setTransfersAvgMonth('₪' + Math.floor(analytics.monthlyAmountAvg).toLocaleString('en-US'));
          }
          if (analytics.historicalMonthlyAmountAvg) {
            setHistoricalTransfersAvg('₪' + Math.floor(analytics.historicalMonthlyAmountAvg).toLocaleString('en-US'));
          }
          if (analytics.activeUsers != null) {
            setActiveUsers(analytics.activeUsers);
          }
          if (analytics.nonActiveUsers != null) {
            setNonActiveUsers(analytics.nonActiveUsers + 1);
          }
          if (analytics.recentTransfers != null) {
            setRecentTransfers(analytics.recentTransfers);
          }

          if (analytics.transfersCount != null) {
            setTransfersCount(analytics.transfersCount);
          }

          if (analytics.usersTransferredCount != null) {
            setUsersTransferredCount(analytics.usersTransferredCount);
          }

          if (analytics.dakot2RealTransfersCount != null) {
            setDakot2RealTransfersCount(analytics.dakot2RealTransfersCount);
            setDakot2SplitTransfersCount(analytics.dakot2SplitTransfersCount);
          }

          if (analytics.previousMonth) {
            if (analytics.previousMonth.monthlyAmountAvg) {
              setTransfersAvgMonthPreviousMonth(
                '₪' + Math.floor(analytics.previousMonth.monthlyAmountAvg).toLocaleString('en-US'),
              );
            }
            if (analytics.previousMonth.transfersCount) {
              setTransfersCountPreviousMonth(analytics.previousMonth.transfersCount);
            }
            if (analytics.previousMonth.usersTransferredCount) {
              setUsersTransferredCountPreviousMonth(analytics.previousMonth.usersTransferredCount);
            }
            if (analytics.previousMonth.monthlySum) {
              setTransfersPreviousMonth('₪' + Math.floor(analytics.previousMonth.monthlySum).toLocaleString('en-US'));
            }
          }
        }
      })
      .catch((e) => {});
  };

  const getGoogleAnalytics = () => {
    AnalyticsApi.getGoogleAnalytics()
      .then((res) => {
        if (res.data.success) {
          let googleAnalytics = res.data.data;
          if (googleAnalytics.monthlyLoginsHistory) {
            setMonthlyLoginsHistory(googleAnalytics.monthlyLoginsHistory);
          }
          if (Array.isArray(googleAnalytics.monthlyHistoryLabels) && googleAnalytics.monthlyHistoryLabels.length > 0) {
            setMonthlyHistoryLabels(googleAnalytics.monthlyHistoryLabels);
          }
        }
      })
      .catch((e) => {});
  };

  useEffect(() => {
    getAnalytics();
    getGoogleAnalytics();
    if (colorMode === 'dark') {
      toggleColorMode();
    }
    setTimeout(() => {
      getUserInfo();
    }, 1000);
  }, [colorMode, toggleColorMode]);

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: '10px', md: '0px' }}>
      <SimpleGrid columns={{ base: 1, md: 2, xl: isSuperAdmin ? 6 : 5 }} gap="16px" mb="16px">
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={greenBg}
              icon={<Icon w="28px" h="28px" as={BiTransfer} color="white" />}
            />
          }
          name="העברות (היום)"
          value={transfersToday}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={blueBg}
              icon={<Icon w="28px" h="28px" as={IoIosCalendar} color="white" />}
            />
          }
          name="העברות (החודש)"
          value={transfersMonth}
          valuePreviousMonth={transfersPreviousMonth}
        />

        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={purpleBg}
              icon={<Icon w="28px" h="28px" as={IoIosCalendar} color="white" />}
            />
          }
          name="העברה חודשית ממוצעת"
          value={transfersAvgMonth}
          valuePreviousMonth={transfersAvgMonthPreviousMonth}
        />

        {isSuperAdmin && (
          <MiniStatistics
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg={orangeBg}
                icon={<Icon w="28px" h="28px" as={IoIosCalendar} color="white" />}
              />
            }
            name="העברה חודשית ממוצעת היסטורית"
            value={historicalTransfersAvg}
          />
        )}

        <MiniStatistics
          nameFontSize={'18px'}
          nameColor={'secondaryGray.900'}
          startContent={
            <Flex
              justifyContent="center"
              align="center"
              direction="column"
              w="56px"
              h="56px"
              borderRadius="28px"
              bg={tealBg}
            >
              <Text fontWeight="bold" fontSize={circleFontSize(usersTransferredCountStr)} color="white">
                {usersTransferredCountStr.toLocaleString('en-US')}
              </Text>
            </Flex>
          }
          name="עובדים העבירו החודש"
          valuePreviousMonth={usersTransferredCountPreviousMonth.toLocaleString('en-US')}
        />

        <MiniStatistics
          nameFontSize={'18px'}
          nameColor={'secondaryGray.900'}
          startContent={
            <Flex
              justifyContent="center"
              align="center"
              direction="column"
              w="56px"
              h="56px"
              borderRadius="28px"
              bg={pinkBg}
            >
              <Text fontWeight="bold" fontSize={circleFontSize(transfersCountStr)} color="white">
                {transfersCountStr}
              </Text>
            </Flex>
          }
          name="העברות החודש"
          valuePreviousMonth={transfersCountPreviousMonth.toLocaleString('en-US')}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap="16px" mb="16px">
        <PieCard activeUsers={activeUsers} nonActiveUsers={nonActiveUsers} />
        <ComplexTable columnsData={columnsDataComplex} tableData={recentTransfers} pageSize={5} />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2, xl: hasGlobalView ? 3 : 2 }} gap="16px" mb="16px">
        <TotalTransfersRecentMonths monthlySum={monthlyTransfersHistory} categories={monthlyHistoryLabels} />
        {hasGlobalView && <MonthlyTransfersCountChart history={monthlyTransfersCountHistory} categories={monthlyHistoryLabels} />}
        <DailyLoginsRecentMonths history={monthlyLoginsHistory} categories={monthlyHistoryLabels} />
      </SimpleGrid>
    </Box>
  );
}
